import { ChevronsUpDown, LogOut, Languages, Check, Clock } from "lucide-react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  PresetsState,
  setTimezone as setTimezoneAction,
} from "@/app/slices/presetsSlice";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { useGetUserQuery } from "@/api/institutional/user_api";
import { getInitials } from "@/lib/utils";

export function NavUser() {
  const { isMobile } = useSidebar();

  const { logout } = useAuth();

  const { t, i18n } = useTranslation();

  const { data: user } = useGetUserQuery({});

  const dispatch = useDispatch();

  const { timezone } = useSelector<any, PresetsState>((state) => state.presets);

  const [openTimezoneDialog, setOpenTimezoneDialog] = useState(false);

  const timezones = (Intl as any).supportedValuesOf("timeZone");

  const setTimezone = (timezone: string) => {
    dispatch(setTimezoneAction(timezone));
    setOpenTimezoneDialog(false);
  };

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage
                    src={"@/assets/svg/crosspoint-logo.svg"}
                    alt={user?.name_first}
                  />
                  <AvatarFallback className="rounded-lg text-primary uppercase">
                    {getInitials(user?.name_first, user?.name_last)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                  <span className="truncate font-semibold capitalize">
                    {user?.name_first}
                  </span>
                  <span className="truncate text-xs">{user?.email}</span>
                </div>
                <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage
                      src={"@/assets/svg/crosspoint-logo.svg"}
                      alt={user?.name_first}
                    />
                    <AvatarFallback className="rounded-lg">
                      {getInitials(user?.name_first, user?.name_last)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate ">{user?.name_first}</span>
                    <span className="truncate text-xs">{user?.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Languages className="mr-2 h-4 w-4" />
                  <span>{t("Language")}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem
                    onClick={() => {
                      if (i18n.language !== "es") {
                        i18n.changeLanguage("es");
                      }
                    }}
                  >
                    <span className="mr-2">🇲🇽</span>
                    <span>Español</span>
                    {i18n.language === "es" && (
                      <Check className="ml-auto h-4 w-4" />
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      if (i18n.language !== "en") {
                        i18n.changeLanguage("en");
                      }
                    }}
                  >
                    <span className="mr-2">🇺🇸</span>
                    <span>English</span>
                    {i18n.language === "en" && (
                      <Check className="ml-auto h-4 w-4" />
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      if (i18n.language !== "zh") {
                        i18n.changeLanguage("zh");
                      }
                    }}
                  >
                    <span className="mr-2">🇨🇳</span>
                    <span>中文</span>
                    {i18n.language === "zh" && (
                      <Check className="ml-auto h-4 w-4" />
                    )}
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  setOpenTimezoneDialog(true);
                }}
              >
                <Clock className="mr-2 h-4 w-4" />
                <span>{t("Timezone")}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {timezone}
                </span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout}>
                <LogOut />
                {t("Log out")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <Dialog open={openTimezoneDialog} onOpenChange={setOpenTimezoneDialog}>
        <DialogContent className="p-0">
          <DialogHeader className="px-4 py-2 border-b">
            <DialogTitle>{t("Select Timezone")}</DialogTitle>
          </DialogHeader>
          <Command>
            <CommandInput placeholder={t("Search timezone...")} />
            <CommandList>
              <CommandEmpty>{t("No results found.")}</CommandEmpty>
              <CommandGroup className="max-h-[300px] overflow-y-auto">
                {timezones.map((tz: string) => (
                  <CommandItem key={tz} value={tz} onSelect={setTimezone}>
                    <Check
                      className={
                        "mr-2 h-4 w-4 " +
                        (timezone === tz ? "opacity-100" : "opacity-0")
                      }
                    />
                    {tz}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
